import type { Tool } from '@xsai/shared-chat'

import type { HeldContextEntry } from './velis-perception'
import type { Reminder } from './velis-reminder'

import { rawTool } from '@xsai/tool'

import { authorizeOperation, observeOperation } from './velis-capability-session'
import { searchWikipedia } from './velis-public-search'
import { applyReminder } from './velis-reminder'

const reminderParameters = {
  type: 'object',
  additionalProperties: false,
  properties: {
    action: { type: 'string', enum: ['list', 'create', 'complete'] },
    id: { type: 'string', description: 'Stable reminder id. Required for create and complete.' },
    text: { type: 'string', description: 'Reminder text. Required for create.' },
    due_at: { type: 'string', description: 'ISO due time. Required for create.' },
    confirmed: { type: 'boolean', description: 'True only after the user confirmed this reminder.' },
  },
  required: ['action', 'id', 'text', 'due_at', 'confirmed'],
}

const searchParameters = {
  type: 'object',
  additionalProperties: false,
  properties: {
    query: { type: 'string', minLength: 2, maxLength: 200, description: 'Public search query.' },
  },
  required: ['query'],
}

const contextParameters = {
  type: 'object',
  additionalProperties: false,
  properties: {},
  required: [],
}

function reminderInput(input: unknown) {
  if (typeof input !== 'object' || input === null)
    return undefined
  const record = input as { action?: unknown, id?: unknown, text?: unknown, due_at?: unknown, confirmed?: unknown }
  if (record.action !== 'list' && record.action !== 'create' && record.action !== 'complete')
    return undefined
  return {
    action: record.action,
    id: typeof record.id === 'string' ? record.id : '',
    text: typeof record.text === 'string' ? record.text : '',
    dueAt: typeof record.due_at === 'string' ? record.due_at : '',
    confirmed: record.confirmed === true,
  }
}

function readGate(operationId: string) {
  const decision = authorizeOperation(operationId)
  if (!decision || decision.allowed)
    return undefined
  return { status: 'rejected' as const, message: decision.reason }
}

function writeGate(operationId: string) {
  const decision = authorizeOperation(operationId)
  if (!decision)
    return { status: 'rejected' as const, message: 'Write is not authorized.' }
  if (decision.allowed)
    return undefined
  return {
    status: decision.status === 'pending-confirmation' ? 'requires_confirmation' as const : 'rejected' as const,
    message: decision.reason,
  }
}

/** Lists reminders. This is a read operation. */
export function createListRemindersTool(load: () => Reminder[]): Tool {
  return rawTool({
    name: 'list_reminders',
    description: 'List reminders stored on this computer.',
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {},
      required: [],
    },
    execute: async () => {
      const block = readGate('list_reminders')
      if (block)
        return block
      return await observeOperation('list_reminders', 800, async () => applyReminder(load(), { action: 'list' }))
    },
  })
}

/** Creates one reminder when the runtime allows the write. */
export function createCreateReminderTool(
  load: () => Reminder[],
  save: (reminders: readonly Reminder[]) => void,
): Tool {
  return rawTool({
    name: 'create_reminder',
    description: 'Create one reminder on this computer. Write only when the user asked for this reminder.',
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: { type: 'string', description: 'Stable reminder id.' },
        text: { type: 'string', description: 'Reminder text.' },
        due_at: { type: 'string', description: 'ISO due time.' },
      },
      required: ['id', 'text', 'due_at'],
    },
    execute: async (input) => {
      const block = writeGate('create_reminder')
      if (block)
        return block
      const command = reminderInput({ ...(typeof input === 'object' && input ? input : {}), action: 'create', confirmed: true })
      if (!command)
        return { status: 'INVALID_INPUT', message: 'A reminder needs an id, text, and due time.' }
      return await observeOperation('create_reminder', 800, async () => {
        const outcome = applyReminder(load(), { action: 'create', id: command.id, text: command.text, dueAt: command.dueAt })
        if (outcome.status === 'saved')
          save(outcome.reminders)
        return outcome
      })
    },
  })
}

/** Completes one reminder when the runtime allows the write. */
export function createCompleteReminderTool(
  load: () => Reminder[],
  save: (reminders: readonly Reminder[]) => void,
): Tool {
  return rawTool({
    name: 'complete_reminder',
    description: 'Mark one reminder complete on this computer. Write only when the user asked to complete it.',
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: { type: 'string', description: 'Reminder id.' },
      },
      required: ['id'],
    },
    execute: async (input) => {
      const block = writeGate('complete_reminder')
      if (block)
        return block
      const id = typeof input === 'object' && input && typeof (input as { id?: unknown }).id === 'string'
        ? (input as { id: string }).id
        : ''
      return await observeOperation('complete_reminder', 800, async () => {
        const outcome = applyReminder(load(), { action: 'complete', id })
        if (outcome.status === 'completed')
          save(outcome.reminders)
        return outcome
      })
    },
  })
}

/** Builds the reminder tool. List is a read. Create and complete wait for confirmation. */
export function createReminderTool(
  load: () => Reminder[],
  save: (reminders: readonly Reminder[]) => void,
): Tool {
  return rawTool({
    name: 'save_reminder',
    description: 'List, create, or complete one reminder on this computer. Call create or complete first with confirmed false. Write only after the user confirms the same id and text.',
    parameters: reminderParameters as unknown as Tool['function']['parameters'],
    execute: async (input) => {
      const command = reminderInput(input)
      if (!command)
        return { status: 'INVALID_INPUT', message: 'A reminder action must be list, create, or complete.' }
      const reminders = load()
      if (command.action === 'list') {
        const block = readGate('list_reminders')
        if (block)
          return block
        return applyReminder(reminders, { action: 'list' })
      }
      const operationId = command.action === 'complete' ? 'complete_reminder' : 'create_reminder'
      const decision = authorizeOperation(operationId)
      if (decision && !decision.allowed) {
        return {
          status: decision.status === 'pending-confirmation' ? 'requires_confirmation' : 'rejected',
          message: decision.reason,
        }
      }
      if (!decision && !command.confirmed) {
        return {
          status: 'requires_confirmation',
          message: `The reminder was not changed. Ask the user to confirm ${command.action} for ${command.id || 'this reminder'}.`,
        }
      }
      const outcome = command.action === 'complete'
        ? applyReminder(reminders, { action: 'complete', id: command.id })
        : applyReminder(reminders, { action: 'create', id: command.id, text: command.text, dueAt: command.dueAt })
      if (outcome.status === 'saved' || outcome.status === 'completed')
        save(outcome.reminders)
      return outcome
    },
  })
}

/** Builds the public search tool. Results come from Wikipedia. */
export function createPublicSearchTool(fetchImpl: typeof fetch = globalThis.fetch): Tool {
  return rawTool({
    name: 'search_public',
    description: 'Search public Wikipedia pages and return titles and URLs. Use this for public facts. Do not use it for private notes.',
    parameters: searchParameters as unknown as Tool['function']['parameters'],
    execute: async (input, options) => {
      const block = readGate('search_public')
      if (block)
        return block
      const query = typeof input === 'object' && input !== null && typeof (input as { query?: unknown }).query === 'string'
        ? (input as { query: string }).query
        : ''
      return await observeOperation('search_public', 1200, () => searchWikipedia(query, fetchImpl, options?.abortSignal))
    },
  })
}

/** Builds the on-demand context read. It returns fresh browser and screen slots only. */
export function createContextReadTool(read: (now?: number) => HeldContextEntry[]): Tool {
  return rawTool({
    name: 'read_held_context',
    description: 'Read the browser or screen context already held for this turn. This does not capture a new page or a new screen.',
    parameters: contextParameters as unknown as Tool['function']['parameters'],
    execute: async () => {
      const entries = read()
      return {
        status: 'ok',
        context: entries.length > 0 ? entries : [{ slot: 'none', summary: 'none' }],
      }
    },
  })
}
